<template>
    <div>
        <div v-if="!loading" class="crew-list-toolbar">
            <div class="crew-status-filter">
                <label for="crew-status-filter">Status</label>
                <select
                    id="crew-status-filter"
                    v-model="selectedStatus"
                    class="form-select"
                    aria-controls="crew-user-list"
                    aria-describedby="crew-status-filter-state"
                    @change="handleStatusFilterChange"
                >
                    <option value="">All</option>
                    <option value="true">Active</option>
                    <option value="false">Disabled</option>
                </select>
                <span id="crew-status-filter-state" class="sr-only" aria-live="polite">
                    Showing {{ selectedStatusLabel.toLowerCase() }} users
                </span>
            </div>
        </div>
        <div id="crew-user-list">
            <CrudIndex
                v-if="!loading"
                ref="crewCrud"
                :permission="PERMISSION_CODES.CMS_VIEW_CREW_MANAGEMENT"
                title="User"
                :viewColumns="columns"
                :fetchUrl="crewFetchUrl"
                createUrl="/users"
                updateRootUrl="/users"
                :defaultPerPage="20"
                :createFields="createFields"
                :dataItemFilter="formatUsers"
                :reloadOnSuccess="true"
                :editingFormatter="editingFormatter"
                @success="handleSuccess"
                :processingCustomActionId="processingCustomActionId"
                :processingCustomAction="processingCustomAction"
                customActionTitle="Email Reset Passcode"
                :customActionPermission="PERMISSION_CODES.CMS_VIEW_TRIGGER_PASSWORD_RESET"
                @handleCustomAction="handleCustomAction"
            />
        </div>
    </div>
</template>

<script>
import CrudIndex from "../../components/crud/index.vue"
import { PERMISSION_CODES } from "../../store/permission"
import { createField, createColumn, errMsg, showSuccessDialog } from "../../utils/common"
import { http } from "@/utils/http"

export default {
    data() {
        return {
            loading: true,
            initData: {},
            selectedStatus: "",
            processingCustomAction: false,
            processingCustomActionId: null,
            createFields: [
                createField("First Name*", "first_name", "text", true),
                createField("Last Name*", "last_name", "text", true),
                createField("Username*", "username", "text", true),
                {
                    ...createField("Passcode", "passcode", "number", false),
                    hint: "4 digits; staff sign in to the POS with it. Set it by editing the user after creating them.",
                },
                createField("Email*", "email", "email", true),
                createField("Phone (10 digits)", "phone", "number", false),
                createField("Country", "country", "country", false),
                createField("State", "state", "state", false),
                createField("City", "city", "city", false),
                createField("Address Line 1", "address_line_1", "text", false),
                createField("Address Line 2", "address_line_2", "text", false),
                createField("Status", "status", "switch", false, true),
            ],
            columns: [
                createColumn("First Name", "first_name", "string"),
                createColumn("Last Name", "last_name", "string"),
                createColumn("Email", "email", "string"),
                createColumn("Status", "statusLabel", "string"),
            ],
            PERMISSION_CODES,
        }
    },
    components: { CrudIndex },
    computed: {
        crewFetchUrl() {
            if (!this.selectedStatus) return "/users"
            return `/users?active=${this.selectedStatus}`
        },
        selectedStatusLabel() {
            if (this.selectedStatus === "true") return "Active"
            if (this.selectedStatus === "false") return "Disabled"
            return "All"
        },
    },
    async mounted() {
        await this.fetchUserInitDatta()
        this.createFields = this.createFields.concat([
            createField("User Image", "user_image", "image", false),
            // createField("Send welcome email", "send_welcome_email", "switch", false),
        ])

        this.loading = false
    },
    methods: {
        async handleStatusFilterChange() {
            await this.$nextTick()
            if (this.$refs.crewCrud) {
                await this.$refs.crewCrud.fetchData()
            }
        },
        formatUsers(users) {
            return users.map((user) => ({
                ...user,
                statusLabel: user.status === false ? "Disabled" : "Active",
            }))
        },
        async handleCustomAction(user) {
            if (!user.email) {
                return this.$toast.error("No email address found!")
            }
            if (this.processingCustomAction) return
            this.processingCustomAction = true
            this.processingCustomActionId = user.id
            try {
                await http.post("/users/reset-passcode", {
                    email: user.email,
                })
                this.$toast.success(`Temporary passcode sent to ${user.email}`)
            } catch (err) {
                console.log("Login err", err)
                this.$toast.error(errMsg(err, "Could not send reset passcode email!"))
            }
            this.processingCustomAction = false
        },
        createdUserDialog(result) {
            const username = (result && result.username) || "the new user"
            const passcodeStep = `Before ${username} can sign in to the POS, edit this user, set a 4-digit passcode, and give it to them.`
            // Queued is an allowed asynchronous attempt; delivery is not yet confirmed.
            if (result && result.welcome_email_status === "queued") {
                return {
                    title: "User created",
                    message: `A welcome email was queued for ${result.email || username}. The email explains how to set up or obtain their 4-digit POS passcode. Delivery is not confirmed.`,
                }
            }
            return {
                title: "User created",
                message: `No welcome email was sent because staff welcome email is disabled. ${passcodeStep}`,
            }
        },
        handleSuccess({ result, editMode }) {
            if (!editMode) {
                setTimeout(() => {
                    const { title, message } = this.createdUserDialog(result)
                    showSuccessDialog(title, message)
                }, 500)
            } else {
                this.$toast.success("User updated successfully")
            }
        },
        editingFormatter(data) {
            return data
        },
        async fetchUserInitDatta() {
            try {
                const res = await http.get("/users/init")
                this.initData = res.data.result
                this.createFields.push(
                    createField(
                        "User Role*",
                        "user_role_id",
                        "select",
                        true,
                        "",
                        this.initData.userRoles.map((role) => ({ id: role.id, title: role.name }))
                    )
                )
                // this.createFields.push(
                //     createField(
                //         "User Type*",
                //         "user_type",
                //         "select",
                //         true,
                //         "",
                //         this.initData.userTypes.map((role) => ({ id: role.name, title: role.name }))
                //     )
                // )
                this.createFields.push(
                    createField(
                        "Warehouse Location*",
                        "location",
                        "select",
                        true,
                        "",
                        this.initData.warehouses.map((role) => ({
                            id: role.name,
                            title: role.name,
                        }))
                    )
                )
            } catch (err) {
                console.log("error in fetching user init data", err)
            }
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

::v-deep .vgt-table th,
::v-deep .vgt-table td {
  padding: $tableCellPaddingY $tableCellPaddingX !important;
}

.crew-list-toolbar {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 12px;
}

.crew-status-filter {
    width: min(100%, 220px);

    label {
        display: block;
        margin-bottom: 4px;
        font-size: 0.8rem;
        font-weight: 600;
    }

    .form-select {
        width: 100%;
    }
}

@media (max-width: 576px) {
    .crew-status-filter {
        width: 100%;
    }
}
</style>
